import { useState } from 'react'
import { createClient, getUserApiKey } from '@/lib/supabase-client'

export default function BlogGeneratorForm() {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [imageStyle, setImageStyle] = useState<'realistic' | 'illustration' | 'abstract' | 'minimalist' | 'photographic'>('realistic')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<any>(null)
  const [copied, setCopied] = useState<string | null>(null)
  const supabase = createClient()
  const apiBaseUrl = 'https://api.gistmag.co.uk'

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const downloadImage = async (imageUrl: string, filename: string) => {
    try {
      // Handle data URLs (base64) and regular URLs
      let blob: Blob
      if (imageUrl.startsWith('data:')) {
        // Convert data URL to blob
        const response = await fetch(imageUrl)
        blob = await response.blob()
      } else {
        // Fetch from URL
        const response = await fetch(imageUrl)
        blob = await response.blob()
      }

      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download image:', err)
      setError('Failed to download image')
    }
  }

  const generateBlogElements = async () => {
    if (!title.trim()) {
      setError('Please enter a blog post title')
      return
    }

    setLoading(true)
    setError(null)
    setResult(null)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setError('Please sign in to use Blog Generator. Sign up for a free account!')
        setLoading(false)
        return
      }

      const apiKey = await getUserApiKey(supabase, session.user.id)

      if (!apiKey) {
        setError('Unable to retrieve API key. Please try refreshing the page.')
        setLoading(false)
        return
      }

      const response = await fetch(`${apiBaseUrl}/blog/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: apiKey,
          title: title,
          content: content || undefined,
          image_style: imageStyle,
          language: 'en',
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        setError(errorData.error || errorData.message || 'Failed to generate blog elements')
        setLoading(false)
        return
      }

      const data = await response.json()
      setResult(data)
    } catch (err: any) {
      setError(err.message || 'An error occurred while generating blog elements')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Gist Blog</h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Generate images, excerpt, category, and tags for your blog posts
      </p>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Blog Post Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter your blog post title..."
          className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Blog Post Content (Optional)
        </label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Enter your blog post content (optional, but helps generate better results)..."
          className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          rows={6}
        />
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {content.length} characters • Providing content helps generate more accurate results
        </p>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Image Style
        </label>
        <select
          value={imageStyle}
          onChange={(e) => setImageStyle(e.target.value as any)}
          className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="realistic">Realistic</option>
          <option value="illustration">Illustration</option>
          <option value="abstract">Abstract</option>
          <option value="minimalist">Minimalist</option>
          <option value="photographic">Photographic</option>
        </select>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <button
        onClick={generateBlogElements}
        disabled={loading || !title.trim()}
        className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {loading ? (
          <>
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Generating...
          </>
        ) : (
          'Generate Meta Elements'
        )}
      </button>

      <p className="mt-3 text-sm text-gray-500 dark:text-gray-400 text-center">
        Cost: 3 credits per generation
      </p>

      {result && (
        <div className="mt-6 space-y-6">
          {/* Images */}
          {result.images && result.images.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Generated Images</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {result.images.map((image: any, index: number) => (
                  <div key={index} className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
                    <div className="relative">
                      <img
                        src={image.url}
                        alt={image.description || `Generated image ${index + 1}`}
                        className="w-full h-48 object-cover"
                      />
                      <button
                        onClick={() => downloadImage(image.url, `blog-image-${index + 1}-${image.style}.png`)}
                        className="absolute top-2 right-2 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 p-2 rounded-lg shadow-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition"
                        title="Download image"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                    </div>
                    <div className="p-3 bg-gray-50 dark:bg-slate-800">
                      <p className="text-xs text-gray-600 dark:text-gray-400 capitalize">{image.style}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Excerpt */}
          {result.excerpt && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Excerpt</h4>
                <button
                  onClick={() => copyToClipboard(result.excerpt, 'excerpt')}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-900/50 transition"
                >
                  {copied === 'excerpt' ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
              <p className="text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
                {result.excerpt}
              </p>
            </div>
          )}

          {/* Category */}
          {result.category && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Category</h4>
                <button
                  onClick={() => copyToClipboard(result.category, 'category')}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-900/50 transition"
                >
                  {copied === 'category' ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
              <span className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 rounded-lg font-medium">
                {result.category}
              </span>
            </div>
          )}

          {/* Tags */}
          {result.tags && result.tags.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Tags</h4>
                <button
                  onClick={() => copyToClipboard(result.tags.join(', '), 'tags')}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-900/50 transition"
                >
                  {copied === 'tags' ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy All
                    </>
                  )}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {result.tags.map((tag: string, index: number) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

